import { useEffect, useRef, useState } from "react";
import { RefreshCw, Loader2, Sparkles, Eye, EyeOff, Plus, Trash2, PlugZap, Search } from "lucide-react";
import { toast } from "sonner";
import { api, money } from "../api";
import { VegDot } from "../components";
import { CATEGORIES } from "./MenuPage";

const LINKABLE = CATEGORIES.filter((c) => c !== "Popular");

const GOLD_TOAST = { background: "#1A1A1A", color: "#D4AF37", border: "1px solid rgba(212,175,55,0.4)" };

const ago = (iso) => {
  if (!iso) return "never";
  const s = Math.max(0, (Date.now() - new Date(iso).getTime()) / 1000);
  if (s < 90) return "just now";
  if (s < 3600) return `${Math.round(s / 60)} min ago`;
  return `${Math.round(s / 3600)} h ago`;
};

const EMPTY_FORM = { name: "", price: "", description: "", image: "", vegetarian: false };

export const AdminExtMenu = () => {
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(null);
  const [q, setQ] = useState("");
  const [adding, setAdding] = useState(false);
  const [form, setForm] = useState(EMPTY_FORM);
  const [saving, setSaving] = useState(false);
  const [confirmDel, setConfirmDel] = useState(null);
  const [storyOpen, setStoryOpen] = useState(null);
  const [catOpen, setCatOpen] = useState(null);
  const [draft, setDraft] = useState("");

  useEffect(() => {
    api.get("/admin/ext-menu").then((r) => setData(r.data)).catch(() => {});
  }, []);

  const patchDish = (d) => setData((p) => ({ ...p, dishes: p.dishes.map((x) => (x.pos_id === d.pos_id ? { ...x, ...d } : x)) }));

  const toggleVisible = async (d) => {
    setBusy(d.pos_id);
    try {
      const r = await api.put(`/admin/ext-menu/${d.pos_id}/visible`, { visible: !d.visible });
      patchDish(r.data);
      toast.success(r.data.visible ? `${d.name} is now on the Passport` : `${d.name} hidden from guests`, { style: GOLD_TOAST });
    } catch (e) { toast.error(e.response?.data?.detail || "Could not update"); }
    setBusy(null);
  };

  const addDish = async () => {
    setSaving(true);
    try {
      const r = await api.post("/admin/ext-menu/manual", {
        name: form.name, price: parseFloat(form.price || "0"),
        description: form.description, image: form.image, vegetarian: form.vegetarian,
      });
      setData((p) => ({ ...p, dishes: [r.data, ...p.dishes] }));
      setForm(EMPTY_FORM);
      setAdding(false);
      toast.success("Dish added — flip SHOW ON PASSPORT when it's ready for guests", { style: GOLD_TOAST });
    } catch (e) { toast.error(e.response?.data?.detail || "Could not add dish"); }
    setSaving(false);
  };

  const removeDish = async (d) => {
    if (confirmDel !== d.pos_id) {
      setConfirmDel(d.pos_id);
      setTimeout(() => setConfirmDel((c) => (c === d.pos_id ? null : c)), 3000);
      return;
    }
    try {
      await api.delete(`/admin/ext-menu/${d.pos_id}`);
      setData((p) => ({ ...p, dishes: p.dishes.filter((x) => x.pos_id !== d.pos_id) }));
      toast.success("Dish removed", { style: GOLD_TOAST });
    } catch (e) { toast.error(e.response?.data?.detail || "Could not remove"); }
    setConfirmDel(null);
  };

  const setCategory = async (d, cat) => {
    setBusy(`cat-${d.pos_id}`);
    try {
      const r = await api.put(`/admin/ext-menu/${d.pos_id}/category`, { category: cat });
      patchDish(r.data);
      setCatOpen(null);
      toast.success(cat ? `${d.name} also shows under ${cat} on the menu` : `${d.name} shows only in the Extended Menu section`, { style: GOLD_TOAST });
    } catch (e) { toast.error(e.response?.data?.detail || "Could not set category"); }
    setBusy(null);
  };

  const saveStory = async (d) => {
    setBusy(`save-${d.pos_id}`);
    try {
      const r = await api.put(`/admin/ext-menu/${d.pos_id}/story`, { story: draft });
      patchDish(r.data);
      setStoryOpen(null);
      toast.success("Story saved", { style: GOLD_TOAST });
    } catch (e) { toast.error(e.response?.data?.detail || "Could not save story"); }
    setBusy(null);
  };

  const regen = async (d) => {
    setBusy(`regen-${d.pos_id}`);
    try {
      const r = await api.post(`/admin/ext-menu/${d.pos_id}/regenerate-story`);
      patchDish(r.data);
      setDraft(r.data.story);
      toast.success("Story written — edit it or save as-is", { style: GOLD_TOAST });
    } catch (e) { toast.error(e.response?.data?.detail || "Could not write the story"); }
    setBusy(null);
  };

  if (!data) return <p className="text-xs text-white/40">Loading…</p>;
  const list = data.dishes.filter((d) => !q.trim() || d.name.toLowerCase().includes(q.toLowerCase()));
  const onCount = data.dishes.filter((d) => d.visible).length;

  return (
    <div className="space-y-3" data-testid="admin-ext-menu">
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <div>
          <p className="text-[10px] tracking-widest font-bold text-white/70">EXTENDED MENU — EXTRA DISHES BEYOND THE MAIN MENU</p>
          <p className="text-[11px] text-white/45" data-testid="ext-on-count">
            {data.dishes.length} dishes · {onCount} showing on Passport — flip a toggle and guests see it instantly
          </p>
        </div>
        <button data-testid="ext-add-dish-btn" onClick={() => setAdding(!adding)}
          className="flex items-center gap-1.5 bg-[#D4AF37] text-black text-[10px] font-black rounded-full px-4 py-2.5 tracking-widest">
          <Plus size={12} /> {adding ? "CLOSE" : "ADD DISH"}
        </button>
      </div>

      {adding && (
        <div className="rounded-xl bg-[#121212] border border-[#D4AF37]/40 p-4 space-y-2" data-testid="ext-add-form">
          <div className="flex gap-2">
            <input data-testid="ext-add-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })}
              placeholder="Dish name" className="flex-1 min-w-0 bg-[#1A1A1A] border border-white/15 rounded-full px-4 py-2.5 text-xs focus:outline-none focus:border-[#D4AF37]/60" />
            <input data-testid="ext-add-price" value={form.price} onChange={(e) => setForm({ ...form, price: e.target.value.replace(/[^0-9.]/g, "") })}
              placeholder="Price" inputMode="decimal" className="w-24 bg-[#1A1A1A] border border-white/15 rounded-full px-4 py-2.5 text-xs focus:outline-none focus:border-[#D4AF37]/60" />
          </div>
          <input data-testid="ext-add-desc" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })}
            placeholder="Short description (optional)" className="w-full bg-[#1A1A1A] border border-white/15 rounded-full px-4 py-2.5 text-xs focus:outline-none focus:border-[#D4AF37]/60" />
          <input data-testid="ext-add-image" value={form.image} onChange={(e) => setForm({ ...form, image: e.target.value })}
            placeholder="Photo URL (optional — https://…)" className="w-full bg-[#1A1A1A] border border-white/15 rounded-full px-4 py-2.5 text-xs focus:outline-none focus:border-[#D4AF37]/60" />
          <div className="flex items-center justify-between gap-2">
            <button data-testid="ext-add-veg" onClick={() => setForm({ ...form, vegetarian: !form.vegetarian })}
              className={`text-[9px] font-bold tracking-widest rounded-full px-3.5 py-2 border ${form.vegetarian ? "border-green-500 text-green-400 bg-green-500/10" : "border-white/20 text-white/50"}`}>
              {form.vegetarian ? "VEGETARIAN ✓" : "VEGETARIAN?"}
            </button>
            <button data-testid="ext-add-save" onClick={addDish} disabled={saving || form.name.trim().length < 2 || !form.price}
              className="bg-[#D4AF37] text-black text-[10px] font-black rounded-full px-6 py-2.5 tracking-widest disabled:opacity-40">
              {saving ? <Loader2 size={11} className="animate-spin" /> : "SAVE DISH"}
            </button>
          </div>
          <p className="text-[10px] text-white/40">New dishes start hidden — guests only see them after you flip SHOW ON PASSPORT.</p>
        </div>
      )}

      {data.dishes.length > 6 && (
        <div className="relative">
          <Search size={14} className="absolute left-4 top-1/2 -translate-y-1/2 opacity-40" />
          <input data-testid="ext-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search dishes…"
            className="w-full bg-[#1A1A1A] border border-white/10 rounded-full pl-10 pr-4 py-2.5 text-xs focus:outline-none focus:border-[#D4AF37]/60" />
        </div>
      )}

      {data.dishes.length === 0 && (
        <div className="rounded-xl border border-white/10 bg-[#121212] p-4" data-testid="ext-waiting">
          <p className="text-xs text-white/60">
            No dishes yet — tap <span className="text-[#D4AF37] font-bold">ADD DISH</span> to create one, or they'll appear here
            automatically once the POS feed is live.
          </p>
        </div>
      )}

      {list.map((d) => (
        <div key={d.pos_id} data-testid={`ext-dish-${d.pos_id}`} className="rounded-xl bg-[#121212] border border-white/10 p-3">
          <div className="flex items-center gap-3">
            {d.image
              ? <img src={d.image} alt={d.name} className="w-11 h-11 rounded-lg object-cover shrink-0" />
              : <span className="w-11 h-11 rounded-lg bg-[#1A1A1A] border border-white/10 shrink-0 flex items-center justify-center text-[#D4AF37]/40"><Sparkles size={15} /></span>}
            <div className="flex-1 min-w-0">
              <p className="text-xs font-bold flex items-center gap-1.5 flex-wrap">
                <VegDot veg={d.vegetarian} />
                <span className="break-words">{d.name}</span>
                <span className={`text-[8px] font-black tracking-widest rounded-full px-1.5 py-0.5 border ${d.source === "manual" ? "border-[#D4AF37]/50 text-[#D4AF37]" : "border-white/20 text-white/40"}`}>
                  {d.source === "manual" ? "ADDED BY YOU" : "FROM POS"}
                </span>
                {d.source !== "manual" && d.live === false && (
                  <span data-testid={`ext-offsale-${d.pos_id}`} className="text-[8px] font-black tracking-widest text-[#E25822]">OFF-SALE ON POS</span>
                )}
              </p>
              <p className="text-[10px] text-white/40 truncate">{money(d.price)}{d.description ? ` · ${d.description}` : ""}</p>
            </div>
            <button data-testid={`ext-category-${d.pos_id}`} onClick={() => setCatOpen(catOpen === d.pos_id ? null : d.pos_id)}
              title={d.linked_category ? `Also shows under ${d.linked_category} — tap to change` : "Not connected to a category — tap to link"}
              className={`shrink-0 hidden sm:flex items-center text-[8px] font-black tracking-widest rounded-full px-2.5 py-2.5 border transition-colors ${d.linked_category ? "border-[#D4AF37]/50 text-[#D4AF37]" : "border-white/15 text-white/35 hover:border-white/30"}`}>
              {d.linked_category ? d.linked_category.toUpperCase() : "NO CATEGORY"}
            </button>
            <button data-testid={`ext-visible-${d.pos_id}`} onClick={() => toggleVisible(d)} disabled={busy === d.pos_id} aria-pressed={d.visible}
              className={`shrink-0 flex items-center gap-1.5 text-[9px] font-black tracking-widest rounded-full px-3.5 py-2.5 border transition-colors ${d.visible ? "bg-[#D4AF37] text-black border-[#D4AF37]" : "text-white/60 border-white/20 hover:border-[#D4AF37]/50"} disabled:opacity-40`}>
              {busy === d.pos_id ? <Loader2 size={10} className="animate-spin" /> : d.visible ? <Eye size={10} /> : <EyeOff size={10} />}
              {d.visible ? "ON PASSPORT" : "SHOW ON PASSPORT"}
            </button>
            {d.source === "manual" && (
              <button data-testid={`ext-delete-${d.pos_id}`} onClick={() => removeDish(d)} aria-label={`Delete ${d.name}`}
                className={`shrink-0 w-9 h-9 rounded-full border flex items-center justify-center ${confirmDel === d.pos_id ? "border-[#E25822] text-[#E25822] bg-[#E25822]/10" : "border-white/15 text-white/40"}`}>
                <Trash2 size={13} />
              </button>
            )}
          </div>
          <div className="mt-1.5">
            <button data-testid={`ext-category-sm-${d.pos_id}`} onClick={() => setCatOpen(catOpen === d.pos_id ? null : d.pos_id)}
              className={`sm:hidden text-[8px] font-black tracking-widest rounded-full px-2.5 py-1.5 border mr-2 ${d.linked_category ? "border-[#D4AF37]/50 text-[#D4AF37]" : "border-white/15 text-white/35"}`}>
              {d.linked_category ? d.linked_category.toUpperCase() : "NO CATEGORY"}
            </button>
            {catOpen === d.pos_id && (
              <div data-testid={`ext-category-picker-${d.pos_id}`} className="border-t border-white/5 mt-1.5 pt-2 pb-1">
                <p className="text-[9px] text-white/40 mb-1.5">Link this dish to a menu category — guests then see it there too. NOT CONNECTED keeps it in the Extended Menu section only.</p>
                <div className="flex flex-wrap gap-1.5">
                  <button data-testid={`ext-category-clear-${d.pos_id}`} onClick={() => setCategory(d, "")} disabled={busy === `cat-${d.pos_id}`}
                    className={`text-[8px] font-black tracking-widest rounded-full px-3 py-2 border ${!d.linked_category ? "bg-white/10 border-white/40 text-white" : "border-white/20 text-white/50"}`}>
                    NOT CONNECTED
                  </button>
                  {LINKABLE.map((c) => (
                    <button key={c} data-testid={`ext-category-pick-${d.pos_id}-${c.toLowerCase().replace(/[^a-z]+/g, "-")}`}
                      onClick={() => setCategory(d, c)} disabled={busy === `cat-${d.pos_id}`}
                      className={`text-[8px] font-black tracking-widest rounded-full px-3 py-2 border ${d.linked_category === c ? "bg-[#D4AF37] text-black border-[#D4AF37]" : "border-[#D4AF37]/30 text-[#D4AF37]/80"}`}>
                      {c.toUpperCase()}
                    </button>
                  ))}
                </div>
              </div>
            )}
            {storyOpen === d.pos_id ? (
              <div className="border-t border-white/5 pt-2">
                <textarea data-testid={`ext-story-input-${d.pos_id}`} value={draft} onChange={(e) => setDraft(e.target.value)} rows={4}
                  placeholder="Optional food story guests can read on the menu…"
                  className="w-full bg-[#1A1A1A] border border-white/10 rounded-xl px-3 py-2 text-xs focus:outline-none focus:border-[#D4AF37]/60" />
                <div className="flex gap-2 mt-1.5">
                  <button data-testid={`ext-story-save-${d.pos_id}`} onClick={() => saveStory(d)} disabled={busy === `save-${d.pos_id}`}
                    className="bg-[#D4AF37] text-black text-[9px] font-black rounded-full px-4 py-2 tracking-widest disabled:opacity-50">SAVE STORY</button>
                  <button data-testid={`ext-story-regen-${d.pos_id}`} onClick={() => regen(d)} disabled={busy === `regen-${d.pos_id}`}
                    className="flex items-center gap-1 text-[9px] font-bold tracking-widest text-[#D4AF37] border border-[#D4AF37]/50 rounded-full px-3.5 py-2 disabled:opacity-50">
                    {busy === `regen-${d.pos_id}` ? <Loader2 size={10} className="animate-spin" /> : <Sparkles size={10} />} WRITE WITH AI
                  </button>
                  <button data-testid={`ext-story-cancel-${d.pos_id}`} onClick={() => setStoryOpen(null)}
                    className="text-[9px] font-bold tracking-widest text-white/50 border border-white/20 rounded-full px-4 py-2">CLOSE</button>
                </div>
              </div>
            ) : (
              <button data-testid={`ext-story-edit-${d.pos_id}`} onClick={() => { setStoryOpen(d.pos_id); setDraft(d.story || ""); }}
                className="text-[9px] font-bold tracking-widest text-white/40 hover:text-[#D4AF37]">
                {d.story ? "STORY ✓ — EDIT" : "+ ADD STORY (OPTIONAL)"}
              </button>
            )}
          </div>
        </div>
      ))}
      {list.length === 0 && data.dishes.length > 0 && <p className="text-xs text-white/40 py-3">No dishes match "{q}".</p>}
    </div>
  );
};

// POS plumbing lives in the TABLET tab now — the Extended Menu list stays clean.
export const PosDiagnostics = () => {
  const [data, setData] = useState(null);
  const [diag, setDiag] = useState(null);
  const [diagKind, setDiagKind] = useState("");
  const [testing, setTesting] = useState(false);
  const [testResult, setTestResult] = useState(null);
  const [gate, setGate] = useState(null);
  const [dry, setDry] = useState(null);
  const [digest86, setDigest86] = useState(null);
  const [gateLog, setGateLog] = useState(null);
  const [showDiff, setShowDiff] = useState(false);
  const [reachedEnd, setReachedEnd] = useState(false);
  const [confirming, setConfirming] = useState(false);
  const tableRef = useRef(null);

  const load = () => api.get("/admin/ext-menu").then((r) => setData(r.data)).catch(() => {});
  const loadDiag = (k = diagKind) =>
    api.get(`/admin/sync-log${k ? `?kind=${k}` : ""}`).then((r) => setDiag(r.data)).catch(() => {});
  const loadGate = () => api.get("/admin/flush-gate").then((r) => setGate(r.data)).catch(() => {});
  const loadDry = () => api.get("/admin/menu-dryrun").then((r) => setDry(r.data)).catch(() => {});
  const loadDigest = () => api.get("/admin/dish-digest").then((r) => setDigest86(r.data)).catch(() => {});
  const loadGateLog = () =>
    api.get("/admin/sync-log?kind=flush-gate").then((r) => setGateLog(r.data.log.slice(0, 6))).catch(() => {});
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); loadDiag(); loadGate(); loadDry(); loadDigest(); loadGateLog(); }, []);
  useEffect(() => {
    const t = setInterval(loadGate, 30000); // the countdown must never show a door that has shut
    return () => clearInterval(t);
  }, []);

  // the gate control only exists once the diff is ACTUALLY reached to the end — not rendered-and-ignored
  useEffect(() => {
    if (showDiff && tableRef.current && tableRef.current.scrollHeight <= tableRef.current.clientHeight + 8)
      setReachedEnd(true);
  }, [showDiff, dry]);
  const onDiffScroll = (e) => {
    const el = e.target;
    if (el.scrollTop + el.clientHeight >= el.scrollHeight - 8) setReachedEnd(true);
  };

  const openGateFromDiff = async () => {
    try {
      await api.post("/admin/flush-gate/open", { minutes: 60, run_id: dry.run_id });
      setShowDiff(false); setConfirming(false);
      loadGate(); loadDiag(); loadGateLog();
      toast.success("Flush gate OPEN for 60 min — it locks itself after", { style: GOLD_TOAST });
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Owner only");
    }
  };

  const printDiff = () => {
    // paper for a midnight decision: black on white, full text, nothing truncated
    const val = (v) => (Array.isArray(v) ? v.join(" | ") : String(v ?? "")) || "—";
    const esc = (s) => String(s).replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;");
    const changes = dry.rows.filter((r) => r.kind === "replaces" || r.kind === "goes_off" || r.kind === "back_on" || r.kind === "fills");
    const kept = dry.rows.filter((r) => r.kind === "blocked");
    const other = dry.rows.filter((r) => r.kind === "unmatched" || r.kind === "skipped");
    const row = (r) => `<div class="row"><b>${esc(r.dish)}</b>${r.field !== "—" ? ` &middot; ${esc(r.field)}` : ""}<div class="w">${esc(r.words)}</div>${
      r.field !== "—" ? `<div class="v">NOW: ${esc(val(r.current))}</div><div class="v">COMING: ${esc(val(r.incoming))}</div>` : ""}</div>`;
    const c = dry.counts;
    const html = `<!doctype html><html><head><title>Dry Run Diff — ${esc(dry.timestamp)}</title><style>
      body{font-family:Georgia,serif;color:#000;background:#fff;margin:24px;font-size:13px}
      h1{font-size:18px;margin:0} .sub{color:#333;margin:4px 0 14px}
      .counts{border:1px solid #000;padding:8px 10px;margin-bottom:14px;font-weight:bold}
      h2{font-size:14px;border-bottom:2px solid #000;padding-bottom:3px;margin:18px 0 8px}
      .row{padding:7px 0;border-bottom:1px solid #bbb;page-break-inside:avoid}
      .w{font-style:italic;margin:2px 0} .v{font-family:monospace;font-size:11px;word-wrap:break-word;white-space:pre-wrap}
    </style></head><body>
    <h1>DRY RUN DIFF — Urban by VeerGi Passport</h1>
    <p class="sub">Run received ${esc(new Date(dry.timestamp).toLocaleString([], { timeZone: "America/Chicago" }))} (Chicago) &middot; ${dry.received} events &middot; printed ${esc(new Date().toLocaleString([], { timeZone: "America/Chicago" }))}</p>
    <div class="counts">${c.dishes_changing} dishes will change &middot; ${c.overwrites} will replace your version &middot; ${c.going_off} going off the menu &middot; ${c.coming_on} coming back on &middot; ${c.fills} blanks filled &middot; ${c.blocked} different — guard keeps yours &middot; ${c.unmatched} unknown keys &middot; ${c.no_change} untouched</div>
    <h2>WILL CHANGE ON A WET FLUSH (${changes.length} rows)</h2>${changes.map(row).join("") || "<p>None.</p>"}
    <h2>DIFFERENT — THE GUARD KEEPS YOUR VERSION, NOTHING CHANGES (${kept.length} rows)</h2>${kept.map(row).join("") || "<p>None.</p>"}
    <h2>UNKNOWN KEYS / NOTHING USABLE (${other.length} rows)</h2>${other.map(row).join("") || "<p>None.</p>"}
    <script>window.onload = () => window.print();</scr` + `ipt></body></html>`;
    const w = window.open("", "_blank");
    if (!w) { toast.error("Allow pop-ups to print the diff"); return; }
    w.document.write(html);
    w.document.close();
  };

  const gateAct = async (open) => {
    try {
      if (open) await api.post("/admin/flush-gate/open", { minutes: 60 });
      else await api.post("/admin/flush-gate/close", {});
      loadGate(); loadDiag();
      toast.success(open ? "Flush gate OPEN for 60 min — bulk menu writes allowed, then it locks itself"
                         : "Flush gate CLOSED — bulk menu writes refused", { style: GOLD_TOAST });
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Gate change failed — owner only");
    }
  };

  const runSelfTest = async () => {
    setTesting(true);
    try {
      const r = await api.post("/admin/integration/self-test");
      setTestResult(r.data);
      if (r.data.ok) toast.success("Connection OK — dry run passed, nothing awarded", { style: GOLD_TOAST });
      else if (r.data.secret_set === false) toast("Waiting for secret — set INTEGRATION_SECRET in both Secrets panels", { style: GOLD_TOAST });
      else toast.error(r.data.detail);
      loadDiag();
    } catch {
      toast.error("Self-test failed to run");
    }
    setTesting(false);
  };

  return (
    <div className="space-y-3" data-testid="pos-diagnostics">
      {data && (
        <div className="rounded-xl border border-white/10 bg-[#1A1A1A] p-4" data-testid="ext-sync-status">
          <div className="flex items-center justify-between gap-2 flex-wrap">
            <div className="min-w-0 flex-1">
              <p className="text-[10px] tracking-widest font-bold text-white/70">VEERGI GALLA POS · EXTENDED MENU · STORED DISHES</p>
              <p className="text-[11px] text-white/45" data-testid="ext-sync-detail">
                Feed retired — the POS pushes the full menu now. These dishes are the stored/manual list; nothing pulls from the POS anymore.
              </p>
            </div>
          </div>
        </div>
      )}

      <div className="rounded-xl bg-[#1A1A1A] border border-white/10 p-4" data-testid="flush-gate-card">
        <div className="flex items-center justify-between gap-2 flex-wrap">
          <div className="min-w-0 flex-1">
            <p className="text-[10px] tracking-widest text-[#D4AF37] font-bold">WET FLUSH GATE — BULK MENU WRITES</p>
            <p className="text-[11px] text-white/45 mt-0.5">
              Locked by default: bulk POS pushes get a machine-readable 423 refusal. Dry runs and
              single-dish writes always pass. Open it only after reading the dry-run diff — it closes itself.
            </p>
            {gate && (
              <p data-testid="flush-gate-state" className={`mt-1.5 text-[11px] font-mono font-bold ${gate.open ? "text-[#FFB020]" : "text-green-400"}`}>
                {gate.open
                  ? `OPEN — ${Math.max(0, Math.ceil((new Date(gate.open_until) - Date.now()) / 60000))} min left (until ${new Date(gate.open_until).toLocaleTimeString([], { hour: "numeric", minute: "2-digit" })}) — opened by ${gate.opened_by} ${ago(gate.opened_at)}${gate.approved_run ? ` — bound to dry run ${gate.approved_run}` : ""}`
                  : `LOCKED${gate.closed_by ? ` — closed by ${gate.closed_by} ${ago(gate.closed_at)}` : gate.opened_at ? ` — last open by ${gate.opened_by} ${ago(gate.opened_at)}, expired` : " — never opened"}`}
              </p>
            )}
          </div>
          {gate?.open ? (
            <button data-testid="flush-gate-close-btn" onClick={() => gateAct(false)}
              className="border border-[#E25822]/60 text-[#E25822] text-[10px] font-bold rounded-full px-4 py-2 tracking-widest shrink-0">
              CLOSE NOW
            </button>
          ) : (
            <button data-testid="flush-gate-open-btn" onClick={() => gateAct(true)}
              className="border border-[#D4AF37]/60 text-[#D4AF37] text-[10px] font-bold rounded-full px-4 py-2 tracking-widest shrink-0">
              OPEN 60 MIN
            </button>
          )}
        </div>
        {(gateLog || []).length > 0 && (
          <div data-testid="gate-history" className="mt-3 pt-2 border-t border-white/10">
            <p className="text-[9px] tracking-widest text-white/35 font-bold">GATE TRAIL</p>
            {gateLog.map((e, i) => (
              <p key={i} data-testid={`gate-history-${i}`} className="text-[11px] font-mono mt-1">
                <span className={e.detail?.startsWith("OPENED") ? "text-[#FFB020]" : "text-green-400"}>
                  {e.detail}
                </span>
                <span className="text-white/35"> · {ago(e.ts)}</span>
              </p>
            ))}
          </div>
        )}
      </div>

      <div className="rounded-xl bg-[#1A1A1A] border border-white/10 p-4" data-testid="admin-86-digest">
        <p className="text-[10px] tracking-widest text-[#D4AF37] font-bold">YESTERDAY'S 86s</p>
        {!digest86 ? null : digest86.lines.length === 0 ? (
          <p className="text-[11px] text-white/45 mt-1.5" data-testid="admin-86-digest-empty">None — a clean night.</p>
        ) : digest86.lines.map((l, i) => {
          const t = (iso) => new Date(iso).toLocaleTimeString([], { timeZone: "America/Chicago", hour: "numeric", minute: "2-digit" });
          const dur = (m) => (m == null ? "" : m >= 60 ? `${Math.floor(m / 60)}h ${m % 60}m` : `${m}m`);
          return (
            <p key={i} data-testid={`admin-digest-line-${i}`} className="text-sm mt-1.5">
              <b>{l.name}</b>
              <span className="text-white/50"> — off {t(l.off_at)}</span>
              {l.still_off
                ? <span className="text-[#FF5A4E] font-black"> · STILL OFF</span>
                : <span className="text-white/50"> → back {t(l.back_at)} ({dur(l.minutes_off)})</span>}
              {l.by && <span className="text-white/35"> · by {l.by}</span>}
            </p>
          );
        })}
      </div>

      <div className="rounded-xl bg-[#1A1A1A] border border-white/10 p-4" data-testid="dryrun-card">
        <div className="flex items-center justify-between gap-2 flex-wrap">
          <div className="min-w-0 flex-1">
            <p className="text-[10px] tracking-widest text-[#D4AF37] font-bold">DRY RUN DIFF — READ BEFORE OPENING THE GATE</p>
            <p className="text-[11px] text-white/45 mt-0.5" data-testid="dryrun-card-detail">
              {dry?.found
                ? `Last dry run ${ago(dry.timestamp)} · ${dry.received} events · ${dry.counts.dishes_changing} dishes would change`
                : "No dry run received yet — the POS sends the flush with X-Dry-Run: true first."}
            </p>
          </div>
          <button data-testid="dryrun-read-btn" disabled={!dry?.found}
            onClick={() => { loadDry(); setReachedEnd(false); setConfirming(false); setShowDiff(true); }}
            className="border border-[#D4AF37]/60 text-[#D4AF37] text-[10px] font-bold rounded-full px-4 py-2 tracking-widest disabled:opacity-40 shrink-0">
            READ THE DIFF
          </button>
        </div>
      </div>

      {showDiff && dry?.found && (
        <div data-testid="dryrun-modal" className="fixed inset-0 z-[90] bg-black/90 flex items-center justify-center p-3">
          <div className="w-full max-w-3xl max-h-[92vh] flex flex-col rounded-2xl bg-[#141419] border border-[#D4AF37]/40 p-4">
            <div className="flex items-start justify-between gap-2">
              <div>
                <p className="text-sm font-black tracking-widest text-[#D4AF37]">DRY RUN DIFF</p>
                <p className="text-[11px] text-white/45">{dry.received} events · received {ago(dry.timestamp)}</p>
              </div>
              <button data-testid="dryrun-close" onClick={() => setShowDiff(false)}
                className="text-white/50 border border-white/20 rounded-full px-3 py-1 text-[10px] font-bold tracking-widest">CLOSE</button>
            </div>
            <button data-testid="dryrun-print-btn" onClick={printDiff}
              className="mt-2 w-full h-10 rounded-full border border-white/25 text-white/70 text-[10px] font-black tracking-widest">
              PRINT THE DIFF — BLACK ON WHITE, FULL TEXT, FOR THE PAPER NEXT TO THE SCREEN
            </button>
            <div data-testid="dryrun-headline" className="flex flex-wrap gap-1.5 mt-3">
              <span className="rounded-full px-2.5 py-1 text-[10px] font-black bg-[#D4AF37]/15 text-[#D4AF37]">{dry.counts.dishes_changing} DISHES WILL CHANGE</span>
              <span className={`rounded-full px-2.5 py-1 text-[10px] font-black ${dry.counts.overwrites > 0 ? "bg-[#FF5A4E]/15 text-[#FF5A4E]" : "bg-white/5 text-white/40"}`}>{dry.counts.overwrites} WILL REPLACE YOUR VERSION</span>
              {dry.counts.going_off > 0 && <span className="rounded-full px-2.5 py-1 text-[10px] font-black bg-[#FF5A4E]/15 text-[#FF5A4E]">{dry.counts.going_off} GOING OFF THE MENU</span>}
              {dry.counts.coming_on > 0 && <span className="rounded-full px-2.5 py-1 text-[10px] font-black bg-[#2ECC71]/15 text-[#2ECC71]">{dry.counts.coming_on} COMING BACK ON</span>}
              <span className="rounded-full px-2.5 py-1 text-[10px] font-black bg-[#2ECC71]/15 text-[#2ECC71]">{dry.counts.fills} BLANKS FILLED</span>
              <span className="rounded-full px-2.5 py-1 text-[10px] font-black bg-[#FFB020]/15 text-[#FFB020]">{dry.counts.blocked} DIFFERENT — GUARD KEEPS YOURS</span>
              <span className="rounded-full px-2.5 py-1 text-[10px] font-black bg-[#E25822]/15 text-[#E25822]">{dry.counts.unmatched} UNKNOWN KEYS</span>
              <span className="rounded-full px-2.5 py-1 text-[10px] font-black bg-white/5 text-white/40">{dry.counts.no_change} UNTOUCHED</span>
            </div>
            <div ref={tableRef} onScroll={onDiffScroll} data-testid="dryrun-table"
              className="flex-1 min-h-0 overflow-y-auto mt-3 rounded-xl bg-black/40 p-3 space-y-2">
              {dry.rows.map((r, i) => (
                <div key={i} data-testid={`dryrun-row-${i}`} className="border-b border-white/8 pb-2">
                  <p className="text-sm">
                    <b>{r.dish}</b>
                    {r.field !== "—" && <span className="text-white/40 font-bold"> · {r.field}</span>}
                  </p>
                  <p className={`text-[11px] font-bold mt-0.5 ${
                    r.kind === "replaces" || r.kind === "goes_off" ? "text-[#FF5A4E]"
                      : r.kind === "blocked" ? "text-[#FFB020]"
                      : r.kind === "fills" || r.kind === "back_on" ? "text-[#2ECC71]"
                      : r.kind === "unmatched" ? "text-[#E25822]" : "text-white/35"}`}>
                    {r.words}
                  </p>
                  {(r.kind === "replaces" || r.kind === "blocked") && (
                    <p className="text-[10px] font-mono text-white/40 mt-1 break-words">
                      NOW: {String(Array.isArray(r.current) ? r.current.join(" | ") : r.current ?? "") || "—"}
                      <br />COMING: {String(Array.isArray(r.incoming) ? r.incoming.join(" | ") : r.incoming ?? "") || "—"}
                    </p>
                  )}
                </div>
              ))}
              <p data-testid="dryrun-end" className="text-center text-[10px] font-black tracking-[0.3em] text-white/30 py-2">— END OF DIFF —</p>
            </div>
            <div className="mt-3">
              {!reachedEnd ? (
                <p data-testid="dryrun-scroll-hint" className="text-[11px] font-bold text-white/40 text-center">
                  Read to the end of the diff — the gate control appears when you get there.
                </p>
              ) : confirming ? (
                <div data-testid="dryrun-confirm" className="rounded-xl border border-[#FF5A4E]/50 bg-[#FF5A4E]/8 p-3">
                  <p className="text-sm font-bold text-[#FF5A4E]">
                    Second confirmation: {dry.counts.overwrites} existing value{dry.counts.overwrites === 1 ? "" : "s"} — content written by hand — will be overwritten.
                    The gate stays open 60 minutes, then locks itself.
                  </p>
                  <div className="flex gap-2 mt-2">
                    <button data-testid="dryrun-confirm-open" onClick={openGateFromDiff}
                      className="flex-1 h-11 rounded-full bg-[#FF5A4E] text-black text-xs font-black tracking-widest">CONFIRM — OPEN THE GATE</button>
                    <button data-testid="dryrun-confirm-cancel" onClick={() => setConfirming(false)}
                      className="h-11 px-5 rounded-full border border-white/20 text-white/60 text-xs font-black tracking-widest">CANCEL</button>
                  </div>
                </div>
              ) : (
                <button data-testid="dryrun-open-gate-btn"
                  onClick={() => (dry.counts.overwrites > 0 ? setConfirming(true) : openGateFromDiff())}
                  className="w-full h-12 rounded-full bg-[#D4AF37] text-black text-xs font-black tracking-widest">
                  OPEN THE GATE — THIS WILL CHANGE {dry.counts.dishes_changing} DISHES AND OVERWRITE {dry.counts.overwrites} EXISTING VALUES
                </button>
              )}
            </div>
          </div>
        </div>
      )}

      <div className="rounded-xl bg-[#1A1A1A] border border-white/10 p-4" data-testid="integration-selftest">
        <div className="flex items-center justify-between gap-2 flex-wrap">
          <div className="min-w-0 flex-1">
            <p className="text-[10px] tracking-widest text-[#D4AF37] font-bold">POS CONNECTION TEST — DRY RUN</p>
            <p className="text-[11px] text-white/45 mt-0.5">Sends a test order through the real order-earn door with your secret. Verifies everything, awards nothing.</p>
          </div>
          <button data-testid="integration-selftest-btn" onClick={runSelfTest} disabled={testing}
            className="flex items-center gap-1.5 border border-[#D4AF37]/60 text-[#D4AF37] text-[10px] font-bold rounded-full px-4 py-2 tracking-widest disabled:opacity-50 shrink-0">
            {testing ? <Loader2 size={11} className="animate-spin" /> : <PlugZap size={11} />} TEST CONNECTION
          </button>
        </div>
        {testResult && (
          <p data-testid="integration-selftest-result"
            className={`mt-2 text-[11px] font-mono leading-snug ${testResult.ok ? "text-green-400" : testResult.secret_set === false ? "text-[#D4AF37]" : "text-[#E25822]"}`}>
            {testResult.ok ? "PASS — " : testResult.secret_set === false ? "WAITING — " : "FAIL — "}{testResult.detail}
          </p>
        )}
      </div>

      {diag && (
        <div className="rounded-xl bg-[#1A1A1A] border border-white/10 p-4" data-testid="ext-sync-diagnostics">
          <div className="flex items-center justify-between gap-2 flex-wrap mb-2">
            <p className="text-[10px] tracking-widest text-[#D4AF37] font-bold">SYNC DIAGNOSTICS — LAST 50 PER KIND</p>
            <div className="flex items-center gap-2">
              <button data-testid="intake-supersede-btn"
                onClick={async () => {
                  if (!window.confirm("Mark every menu-intake event from BEFORE the last FULL menu replace as superseded? Nothing is deleted — rows stay in history, dimmed.")) return;
                  try {
                    const r = await api.post("/admin/menu-intake/supersede");
                    toast.success(`Superseded ${r.data.sync_rows_marked} stale events — replace ${r.data.superseded_by} is the menu truth`);
                    loadDiag();
                  } catch (err) {
                    toast.error(err.response?.data?.detail || "Could not supersede");
                  }
                }}
                className="text-[9px] font-bold tracking-widest text-[#D4AF37] border border-[#D4AF37]/50 rounded-full px-3 py-1">
                MARK PRE-REPLACE AS SUPERSEDED
              </button>
              <button data-testid="sync-diag-refresh" onClick={() => loadDiag()}
                className="text-[9px] font-bold tracking-widest text-white/50 border border-white/20 rounded-full px-3 py-1">REFRESH</button>
            </div>
          </div>
          <div className="flex items-center gap-1.5 mb-2 flex-wrap" data-testid="sync-diag-filters">
            {[["", "ALL"], ["order-earn", "ORDER-EARN"], ["menu-intake", "MENU-INTAKE"]].map(([k, label]) => (
              <button key={label} data-testid={`sync-filter-${label.toLowerCase()}`}
                onClick={() => { setDiagKind(k); loadDiag(k); }}
                className={`text-[9px] font-bold tracking-widest rounded-full px-3 py-1 border ${diagKind === k ? "border-[#D4AF37] text-[#D4AF37] bg-[#D4AF37]/10" : "border-white/15 text-white/50"}`}>
                {label} {k ? `(${(diag.kinds || {})[k] || 0})` : `(${Object.values(diag.kinds || {}).reduce((a, b) => a + b, 0)})`}
              </button>
            ))}
          </div>
          <p className="text-[10px] text-white/50 mb-2">
            Handshake: <span data-testid="diag-sync-enabled" className={diag.sync_enabled ? "text-green-400" : "text-[#E25822]"}>{diag.sync_enabled ? "ENABLED" : "DISABLED (pos_sync flag)"}</span>
            {" "}· Secret: <span data-testid="diag-secret" className={diag.secret_configured ? "text-green-400" : "text-[#E25822]"}>{diag.secret_configured ? "CONFIGURED" : "NOT SET — order-earn returns 503"}</span>
            {" "}· Orders received: {diag.orders_received} · pending retry: {diag.orders_pending}
          </p>
          <div className="max-h-56 overflow-y-auto space-y-1" data-testid="sync-log-list">
            {diag.log.length === 0 && <p className="text-[10px] text-white/35">No sync attempts yet.</p>}
            {diag.log.map((e, i) => (
              <div key={i} className={`text-[10px] font-mono leading-snug ${e.superseded ? "opacity-40" : ""}`}>
                {e.superseded && <span data-testid={`superseded-tag-${i}`} className="text-white/60 font-black">[SUPERSEDED · {e.superseded_by}] </span>}
                <span className={e.ok ? "text-green-400" : "text-[#E25822]"}>{e.ok ? "OK " : "ERR"}</span>
                <span className="text-white/35"> {String(e.ts).slice(0, 19).replace("T", " ")} </span>
                <span className="text-[#D4AF37]">{e.kind}</span>
                {e.kind === "menu-intake" && e.applied != null ? (
                  <span data-testid={`intake-row-${i}`}>
                    {e.dry_run && <span className="text-[#D4AF37] font-black"> DRY RUN — nothing written ·</span>}
                    <span className="text-green-400"> applied {e.applied}</span>
                    <span className="text-[#E25822]"> · unmatched {e.unmatched}</span>
                    <span className="text-white/50"> · skipped {e.skipped}</span>
                    {e.guarded > 0 && <span className="text-[#D4AF37]"> · guarded {e.guarded}</span>}
                    {(e.unmatched_keys || []).length > 0 && (
                      <span className="text-[#E25822]/80 break-all"> — {e.unmatched_keys.join(", ")}</span>
                    )}
                    {(e.ignored_fields || []).length > 0 && (
                      <span className="text-white/40 break-all"> · ignored: {e.ignored_fields.join(", ")}</span>
                    )}
                  </span>
                ) : (
                  <span className="text-white/60"> — {e.detail}</span>
                )}
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};
